<template>
  <div>
    <div class="page-head">
      <div>
        <h1 class="page-head__title">{{ t("webhooks.title") }}</h1>
        <p class="page-head__lede">{{ t("webhooks.lede") }}</p>
      </div>
    </div>

    <div class="card">
      <h2 class="card__title">{{ t("webhooks.howTitle") }}</h2>
      <ol class="wh__steps small">
        <li>{{ t("webhooks.how1") }}</li>
        <li>{{ t("webhooks.how2") }}</li>
        <li>{{ t("webhooks.how3") }}</li>
        <li>{{ t("webhooks.how4") }}</li>
      </ol>
      <pre class="code-block">{{ SNIPPET }}</pre>
      <p class="small muted">
        {{ t("webhooks.whyNotHere") }}
        <router-link to="/docs/guides#webhooks">{{ t("webhooks.readGuide") }}</router-link>
      </p>
    </div>

    <div class="card">
      <div class="card__head">
        <div>
          <h2 class="card__title">{{ t("webhooks.topics") }}</h2>
          <p class="card__sub">{{ t("webhooks.topicsLede") }}</p>
        </div>
      </div>

      <div v-if="error" class="notice notice--danger"><div class="notice__body">{{ error }}</div></div>
      <div v-else-if="loading" class="row"><span class="spinner" aria-hidden="true"></span><span class="muted small">{{ t("common.loading") }}</span></div>

      <template v-else>
        <section v-for="group in groups" :key="group.name" class="wh__group">
          <h3 class="wh__group-title">{{ group.name }}</h3>
          <div class="table__wrap">
            <table class="table">
              <thead>
                <tr><th>{{ t("webhooks.topic") }}</th><th>{{ t("webhooks.scope") }}</th><th>{{ t("webhooks.description") }}</th></tr>
              </thead>
              <tbody>
                <tr v-for="topic in group.topics" :key="topic.name">
                  <td>
                    <code class="xs">{{ topic.name }}</code>
                    <div class="row row--wrap wh__flags">
                      <span v-if="topic.available === false" class="badge">{{ t("webhooks.comingSoon") }}</span>
                      <span v-if="topic.high_volume" class="badge badge--warn">{{ t("webhooks.highVolume") }}</span>
                      <span v-if="topic.requires_unit_filter" class="badge badge--info">{{ t("webhooks.unitFilter") }}</span>
                    </div>
                  </td>
                  <td><code class="xs">{{ topic.required_scope || topic.scope || "—" }}</code></td>
                  <td class="small">
                    {{ topic.description }}
                    <div v-if="topic.payload_fields && topic.payload_fields.length" class="xs muted">
                      {{ t("webhooks.fields") }}: {{ fieldNames(topic.payload_fields) }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { api } from "../lib/api";
import { useErrorText } from "../lib/errors";

const { t } = useI18n();
const errorText = useErrorText();

// Delivery logs and replay live on each installation; the hub never sees them.
const SNIPPET = `POST {api_base}/ext/v1/webhooks            # token with webhooks:manage
Authorization: Bearer <access token for that installation>
Idempotency-Key: <uuid>

{ "url": "https://app.example.com/webhooks/zarokaa",
  "topics": ["unit.updated", "install.uninstalled"] }

GET  {api_base}/ext/v1/webhooks/{id}/deliveries
POST {api_base}/ext/v1/webhooks/deliveries/{delivery_id}/replay`;

const topics = ref([]);
const loading = ref(true);
const error = ref("");

const groups = computed(() => {
  const map = new Map();
  for (const topic of topics.value) {
    const name = topic.group || topic.name.split(".")[0];
    if (!map.has(name)) map.set(name, []);
    map.get(name).push(topic);
  }
  return [...map.entries()].map(([name, list]) => ({ name, topics: list }));
});

function fieldNames(fields) {
  return fields.map((f) => (typeof f === "string" ? f : f.name)).filter(Boolean).join(", ");
}

onMounted(async () => {
  try {
    topics.value = (await api.webhookTopics()) || [];
  } catch (e) {
    error.value = errorText(e);
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.wh__steps { padding-left: var(--space-5); margin: var(--space-3) 0; }
.wh__steps li { margin-bottom: var(--space-2); }
.wh__group + .wh__group { margin-top: var(--space-5); }
.wh__group-title { font-size: var(--text-sm); color: var(--ink-muted); text-transform: uppercase; letter-spacing: .04em; }
.wh__flags { gap: var(--space-1); margin-top: var(--space-1); }
</style>
